import React, { useEffect, useMemo, useRef, useState } from "react";

import { DateTime } from "luxon";

import { useTranslation } from "react-i18next";

import { Toast } from "primereact/toast";

import { systemService } from "../services/systemService";
import { assetService } from "../services/assetService";
import { transactionService } from "../services/transactionService";
import { Partner, partnerService } from "../services/partnerService";
import { partnerWellSuperAppService } from "../services/partnerWellSuperAppService";
import { partnerSatService } from "../services/partnerSatService";
import { partnerThaiLifeService } from "../services/partnerThaiLifeService";
import { partnerLineService } from "../services/partnerLineService";
import { partnerAliveService } from "../services/partnerAliveService";
import { loggingService, logLobbyAction } from "../services/logService";

import LoadingModal from "../components/modals/LoadingModal";

import { useNavigate } from "react-router";

import { hospitalRedirectService } from "../utils/hospitalRedirectService";

interface RegistrationModel {
  HNNumber: string;
  FirstName: string;
  LastName: string;
  IDCard: string;
  BirthDate: string;
  Gender: string;
  Tel: string;
  Email: string;
  Contact: string;
  AgreementName: string;
  Symptom: string;
  [key: string]: string;
}

const Callback: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toastRef = useRef<Toast>(null);

  const navigate = useNavigate();

  const [logo, setLogo] = useState<string>();
  const [system, setSystem] = useState(
    systemService.getSystem(window.location)
  );
  const [partner, setPartner] = useState<undefined | Partner>();
  const [isLoading, setIsLoading] = useState(false);
  const [requestReceived, setRequestReceived] = useState(false);

  const [registrationModel, setRegistrationModel] = useState<RegistrationModel>(
    {
      HNNumber: "",
      FirstName: "",
      LastName: "",
      IDCard: "",
      BirthDate: DateTime.local().toISODate(),
      Gender: "ไม่ระบุ",
      Tel: "",
      Email: "",
      Contact: "",
      AgreementName: "UsageTerms",
      Symptom: "",
    }
  );

  const registrationForm = useMemo(
    () => [
      { name: "ชื่อ", nameEn: "First Name", modelMap: "FirstName" },
      { name: "นามสกุล", nameEn: "Last Name", modelMap: "LastName" },
      { name: "บัตรประชาชน", nameEn: "ID card", modelMap: "IDCard" },
      { name: "วันเกิด", nameEn: "Date of Birth", modelMap: "BirthDate" },
      { name: "เบอร์โทรศัพท์", nameEn: "Phone Number", modelMap: "Tel" },
      { name: "อีเมล", nameEn: "Email", modelMap: "Email" },
    ],
    []
  );

  const hasValue = (field: any) => {
    const value = registrationModel[field.modelMap];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "" || trimmed === "ไม่ระบุ") return false;
      if (
        field.modelMap === "BirthDate" &&
        value === DateTime.local().toISODate()
      )
        return false;
      return true;
    }
    return false;
  };

  const meaningfulFields = registrationForm.filter((f) => hasValue(f));

  const onMountedPartnerUx = async () => {
    const currentPartner = partnerService.getPartner();
    // ISO27018: do not log partner object (contains userData with PII)
    // console.log("Mounted partner:", currentPartner);

    if (!currentPartner) {
      navigate("/");
      return true;
    }

    setPartner(currentPartner);
    return false;
  };

  const onMountedPartnerUi = (currentPartner: Partner) => {
    if (currentPartner) {
      setPartner(currentPartner);

      // Set logo
      setLogo(assetService.getLogo(currentPartner.name));

      // Set language
      // i18n.changeLanguage(system.language);

      // Prefill data
      if (currentPartner.userData) {
        setRegistrationModel((prev) => ({
          ...prev,
          FirstName: currentPartner.userData?.firstName ?? "",
          LastName: currentPartner.userData?.lastName ?? "",
          IDCard: currentPartner.userData?.idCard ?? "",
          BirthDate: currentPartner.userData?.birthdate ?? "",
          Tel: currentPartner.userData?.tel ?? "",
          Email: currentPartner.userData?.email ?? "",
          HNNumber: currentPartner.userData?.hn ?? "",
        }));
      }
    }
  };

  useEffect(() => {
    (async () => {
      const stop = await onMountedPartnerUx();
      if (stop) return;

      const currentPartner = partnerService.getPartner();
      if (currentPartner) {
        onMountedPartnerUi(currentPartner);
      }
    })();
  }, []);

  const submit = async () => {
    if (!partner) return;

    // Validation
    // if (!registrationModel.Symptom.trim()) {
    //   toastRef.current?.show({
    //     severity: "warn",
    //     summary: t("pages.callback.warning.title") || "Warning",
    //     detail:
    //       t("pages.callback.warning.message") ||
    //       "Please describe your symptom.",
    //     life: 3000,
    //   });
    //   return;
    // }

    setIsLoading(true);

    // use loading modal instead of toast for processing
    // toastRef.current?.show({
    //   severity: "info",
    //   summary: t("pages.callback.texts.processing") || "Processing...",
    //   detail:
    //     t("pages.callback.texts.pleaseWait") ||
    //     "Please wait while we process your request.",
    //   life: 2000,
    // });

    try {
      await Promise.race([
        transactionService.requestCallback(registrationModel.Symptom),
        new Promise((res) => setTimeout(res, 5000)),
      ]);

      setRequestReceived(true);

      // logging API: submitted_callback
      await loggingService(
        "submitted_callback",
        {
          message: "[Submit] Callback request submitted",
          data: registrationModel,
        },
        "[Submit] Logging submitted_callback failed"
      );

      // show success page instead of toast for success
      // toastRef.current?.show({
      //   severity: "success",
      //   summary: t("pages.callback.texts.successTitle") || "Request Received",
      //   detail:
      //     t("pages.callback.texts.successMessage") ||
      //     "Your request has been successfully submitted.",
      //   life: 3000,
      // });
    } catch (err) {
      toastRef.current?.show({
        severity: "error",
        summary: t("pages.callback.notifications.submitError.title") || "Error",
        detail:
          t("pages.callback.notifications.submitError.message") ||
          "An error occurred while processing your request.",
        life: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const successOnPage = async () => {
    // Partner: Well Super App.
    if (partnerWellSuperAppService.isActive()) {
      window.location.href = "https://well.dhv.life/";
    }

    // Partner: SAT.
    if (partnerSatService.isActive()) {
      window.location.href = "https://www.sat.or.th";
    }

    // Partner: ThaiLife.
    if (partnerThaiLifeService.isActive()) {
      window.location.href = "https://thailifeapplication.page.link/Samitivej";
    }

    // Partner: Line.
    if (partnerLineService.isActive()) {
      window.location.href = partnerLineService.getReturnUrl();
    }

    // Partner: Alive.
    if (partnerAliveService.isActive()) {
      const deepLinkUrl = partnerAliveService.getDeepLinkUrl();
      const status = transactionService.getTransactionStatus();
      if (status === 7) {
        // Redirect to web payment url with deep link url attached via query param.
        const webPaymentUrl = process.env.WEB_PAYMENT_URL;
        const encoded = encodeURIComponent(deepLinkUrl);
        window.location.href = `${webPaymentUrl}?partner=ALive&deepLinkUrl=${encoded}`;
      } else {
        // Redirect to deep link url.
        window.location.href = deepLinkUrl;
      }
    }

    // Hospital-specific redirects (STH, SSH, SCH, SCT)
    const hospitalRedirectUrl = hospitalRedirectService.getRedirectUrl(
      partner?.name || "",
      localStorage.getItem("lang") ?? ""
    );
    if (hospitalRedirectUrl) {
      window.location.href = hospitalRedirectUrl;
      return;
    }

    // Partner: Webview.
    window.location.href =
      system.language === "en"
        ? "https://www.samitivejhospitals.com/center/detail/Virtual-Hospital"
        : "https://www.samitivejhospitals.com/th/center/detail/Virtual-Hospital";
  };

  const success = () => {
    navigate("/success");
  };

  return (
    <div className="flex flex-col items-center p-4 md:p-6 gap-y-4 justify-center min-h-screen w-full">
      <div className="flex flex-col items-center gap-y-4 w-full bg-white">
        <Toast ref={toastRef} position="top-center" />
        {isLoading && (
          <LoadingModal
            message={t("pages.callback.texts.processing") || "Processing..."}
          />
        )}

        <div className="max-w-3xl w-full border p-10 rounded-3xl shadow-md bg-white items-center flex flex-col">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            {logo && (
              <img
                src={logo}
                alt="Logo"
                width={172}
                height={64}
                className="object-contain"
              />
            )}
          </div>

          {!requestReceived ? (
            <>
              <h2 className="text-center text-xl font-semibold mb-4">
                {t("pages.callback.texts.illnessMessage")}
              </h2>

              {/* Symptom */}
              <textarea
                className="w-full border rounded-2xl p-3 text-lg mb-3 focus:ring-2 focus:ring-blue-300"
                rows={4}
                placeholder={
                  t("pages.callback.texts.symptomPlaceholder") ||
                  "Please describe your symptom"
                }
                value={registrationModel.Symptom}
                onChange={(e) =>
                  setRegistrationModel((prev) => ({
                    ...prev,
                    Symptom: e.target.value,
                  }))
                }
              />

              {/* Dynamic fields */}
              {meaningfulFields.map((item) => (
                <div key={item.modelMap} className="mb-3 w-full">
                  <label className="block text-gray-700 font-medium mb-1">
                    {t(`pages.callback.fields.${item.modelMap}`)}
                  </label>
                  <input
                    readOnly
                    value={registrationModel[item.modelMap]}
                    className="w-full bg-gray-100 border rounded-xl py-2 px-3"
                    placeholder={t(
                      `pages.callback.placeholders.${item.modelMap.toLowerCase()}`
                    )}
                  />
                </div>
              ))}

              {/* Buttons */}
              <div className="flex gap-3 mt-6 w-full">
                <button
                  onClick={success}
                  className="flex-1 py-3 rounded-full bg-gray-100 border border-gray-300 text-gray-700 text-lg hover:bg-gray-200 transition"
                >
                  {t("pages.callback.buttons.cancel")}
                </button>
                <button
                  onClick={submit}
                  className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#E0BB84] via-[#E9D4B3] to-[#C19E67] text-black text-lg font-medium shadow hover:scale-[1.02] transition"
                >
                  {t("pages.callback.buttons.confirm")}
                </button>
              </div>

              <p className="text-center text-gray-400 mt-6 text-sm">
                {t("generals.copyright")}
              </p>
            </>
          ) : (
            <div className="text-center">
              <div className="text-xl mb-8 leading-relaxed">
                {t("pages.callback.texts.received1")}
                <br />
                {t("pages.callback.texts.received2")}
                <br />
                {t("pages.callback.texts.received3")}
              </div>
              <button
                onClick={successOnPage}
                className="bg-gradient-to-r from-[#E0BB84] via-[#E9D4B3] to-[#C19E67] text-black rounded-full py-3 px-10 text-lg font-medium shadow hover:scale-[1.02] transition"
              >
                {t("pages.callback.buttons.finish")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Callback;
